import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import NavbarMenu from "../../components/heroSection/NavbarMenu";
import {
  FaUser,
  FaRoad,
  FaAddressCard,
  FaLock,
  FaEnvelope,
  FaEye, 
  FaEyeSlash,
} from "react-icons/fa";
import "../registerDriverPage/RegisterDriverPage.css";
import Footer from "../../components/footer/Footer";
import { authAPI } from "../../api/apiSystem";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../SEO";

export default function RegisterClientPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State للحقول
  const [formData, setFormData] = useState({
    company_name: "",
    firstname: "",
    lastname: "",
    nicname: "",
    email: "",
    password: "",
    street_address: "",
    addressone: "",
    postal_code: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    password: false,
  });

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: "", color: "#e5e7eb" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2)
      return { level: 1, label: t("common.weak", "Weak"), color: "#dc2626" };
    if (score === 3)
      return { level: 2, label: t("common.medium", "Medium"), color: "#d97706" };
    if (score === 4)
      return { level: 3, label: t("common.strong", "Strong"), color: "#16a34a" };
    return { level: 4, label: t("common.veryStrong", "Very strong"), color: "#059669" };
  };

  const [loading, setLoading] = useState(false);

  // تحديث الحقول
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // إرسال البيانات
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await authAPI.registerUser(formData);

      if (res.success) {

        localStorage.setItem("confirm_email", formData.email);
        
        // ✅ نجاح التسجيل
        Swal.fire({
          icon: "success",
          title: t("RegisterClientPage.success_message.title"),
          text: t("RegisterClientPage.success_message.text"),
          confirmButtonText: t(
            "RegisterClientPage.success_message.confirm_button",
          ),
        }).then(() => {
          navigate("/otp-verification");
        });

        setFormData({
          company_name: "",
          firstname: "",
          lastname: "",
          nicname: "",
          email: "",
          password: "",
          street_address: "",
          addressone: "",
          postal_code: "",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("RegisterClientPage.error_message.title"),
          text: res.message || t("RegisterClientPage.error_message.text"),
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("RegisterClientPage.error_message.title"),
        text: t("RegisterClientPage.error_message.server_error"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="TransCirox – Als Kunde registrieren | Transport einfach buchen"
        description="Erstellen Sie Ihr Kundenkonto bei TransCirox und buchen Sie bequem Umzüge, Möbeltransporte und Gütertransporte."
        keywords="Kunden registrieren, Umzug buchen, Transport buchen, Kundenkonto"
      />
      <NavbarMenu />

      <div
        className="register-driver-section"
        style={{minHeight: "100vh" }}
      >
        <Container>
          <Row className="justify-content-center align-items-center h-100">
            <Col md={10} lg={8}>
              <Card className="shadow-lg p-4 border-0 dark-register-driver-card">
                <Card.Body>
                  <h3 className="text-center mb-4 text-dark fw-bold">
                    {t("RegisterClientPage.form_title")}
                  </h3>

                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Group controlId="formCompanyName">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.company_name")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaAddressCard className="form-icon" />
                            <Form.Control
                              type="text"
                              name="company_name"
                              value={formData.company_name}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.company_name",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formFirstName">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.first_name")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="firstname"
                              value={formData.firstname}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.first_name",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formLastName">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.last_name")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="lastname"
                              value={formData.lastname}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.last_name",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formNicname">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.nickname")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaUser className="form-icon" />
                            <Form.Control
                              type="text"
                              name="nicname"
                              value={formData.nicname}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.nickname",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formStreet">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.street_address")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaRoad className="form-icon" />
                            <Form.Control
                              type="text"
                              name="street_address"
                              value={formData.street_address}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.street_address",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formAddressOne">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.address_one")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaAddressCard className="form-icon" />
                            <Form.Control
                              type="text"
                              name="addressone"
                              value={formData.addressone}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.address_one",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formPostalCode">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.postal_code")}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="postal_code"
                            value={formData.postal_code}
                            onChange={handleChange}
                            placeholder={t(
                              "RegisterClientPage.form_fields.placeholders.postal_code",
                            )}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formEmail">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.email")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaEnvelope className="form-icon" />
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.email",
                              )}
                              className="ps-5"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="formPassword">
                          <Form.Label>
                            {t("RegisterClientPage.form_fields.password")}
                          </Form.Label>
                          <div className="position-relative">
                            <FaLock className="form-icon" />
                            <Form.Control
                              type={showPasswords.password ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder={t(
                                "RegisterClientPage.form_fields.placeholders.password",
                              )}
                              className="ps-5"
                            />
                          <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("password")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.password
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          {formData.password && (
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-1">
                                <small className="text-muted">{t("common.passwordStrength", "Password strength")}</small>
                                <small style={{ color: getPasswordStrength(formData.password).color, fontWeight: 600 }}>
                                  {getPasswordStrength(formData.password).label}
                                </small>
                              </div>
                              <div className="password-strength-bar">
                                <div
                                  className="password-strength-fill"
                                  style={{
                                    width: `${(getPasswordStrength(formData.password).level / 4) * 100}%`,
                                    backgroundColor: getPasswordStrength(formData.password).color,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      </Col>

                      <Col xs={12} className="mt-3">
                        <Button
                          type="submit"
                          variant="warning"
                          className="w-100 bg-orange-500 text-white"
                          disabled={loading}
                        >
                          {loading
                            ? t("RegisterClientPage.button.submitting")
                            : t("RegisterClientPage.button.submit")}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Footer />
    </>
  );
}
